import React, {useMemo} from 'react'
import {AssembledCard} from '../../../common/types'
import {ThemePayload} from '../../../common/themes'

import {PreviewRenderer as OneSixFour} from './versions/1.6.4'
import {PreviewRenderer as TwentyThreeThreeFive} from './versions/23.3.5'
import {PreviewRenderer as TwentyThreeFourThree} from './versions/23.4.3'
import {PreviewRenderer as TwentyFourOneOh} from './versions/24.1.0'

type SdkType = 'stable' | 'beta'

type Version = {
  id: string
  type: SdkType
  current?: boolean
  preview?: boolean
}

// Update this when adding a new version!
// Note: if strange rendering issues switching between versions
// ensure each version uses unique classnames in picostyles
// see https://atomic-app.slack.com/archives/CFSA70TA8/p1696468334350439?thread_ts=1696306867.391569&cid=CFSA70TA8
const versions: (Version & {renderer: unknown})[] = [
  {
    id: '24.1.0',
    type: 'stable' as const,
    renderer: TwentyFourOneOh,
    current: true
  },
  {
    id: '23.4.3',
    type: 'stable' as const,
    renderer: TwentyThreeFourThree
  },
  {
    id: '23.3.5',
    type: 'stable' as const,
    renderer: TwentyThreeThreeFive
  },
  {
    id: '1.6.4',
    type: 'stable' as const,
    renderer: OneSixFour
  }
]

// Currently all preview-renderer scripts take the same input
// if that changes we will need to get smarter here.
interface PreviewRendererProps {
  cardDefinition: AssembledCard
  currentSubviewId: string | null
  onNavigate: (path: string | null) => void
  theme: ThemePayload
}
type RendererProps = PreviewRendererProps & {sdkVersion: string}
type Renderer = React.ComponentType<PreviewRendererProps>

export const PreviewRendererChooser = ({
  sdkVersion,
  ...props
}: RendererProps) => {
  const versionToRenderer = useMemo(() => {
    return versions.reduce(
      (mut_acc, v) => {
        mut_acc[v.id] = v.renderer as Renderer // by casting here, we avoid the need for type def file per SDK version
        return mut_acc
      },
      {} as Record<string, Renderer>
    )
  }, [])

  const PreviewRenderer = versionToRenderer[sdkVersion]

  if (!Object.keys(versionToRenderer).includes(sdkVersion)) {
    throw new Error(`Renderer version ${sdkVersion} not found`)
  }

  if (!PreviewRenderer) {
    throw new Error(`Renderer for ${sdkVersion} is falsey`)
  }

  return (
    <PreviewRenderer
      cardDefinition={props.cardDefinition}
      currentSubviewId={props.currentSubviewId}
      onNavigate={props.onNavigate}
      theme={props.theme}
    />
  )
}

export const getVersions = (): Version[] =>
  versions.map(v => ({
    id: v.id,
    type: v.type,
    preview: v.preview,
    current: v.current
  }))

export const getCurrentDefault = (type: SdkType): string => {
  const version = versions.find(v => v.type === type && v.current)?.id
  if (!version) {
    throw new Error(`A current version must be defined for ${type}`)
  }
  return version
}
